'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { Search, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries/en';
import { localizedHref } from '@/lib/i18n/href';

interface HeaderProps {
  locale: Locale;
  dict: Dictionary;
  // Locales actually reachable right now (see lib/i18n/config.ts's
  // getEnabledLocales). Optional + defaulted to [locale] so nothing breaks
  // if a future call site forgets to pass it — that just hides the switcher
  // instead of offering a language that immediately redirects back.
  enabledLocales?: Locale[];
  aboutUrl?: string;
  contactUrl?: string;
  privacyUrl?: string;
  termsUrl?: string;
}

export default function Header({
  locale,
  dict,
  enabledLocales = [locale],
}: HeaderProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState('');

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  const home = localizedHref(locale, '/');

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
  }, [searchParams]);

  // Clear any pending debounced navigation on unmount so it can't fire
  // after the component (and its `pathname`/`router` closures) are gone.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Autofocus the mobile search input the moment it expands, so tapping the
  // icon drops the user straight into typing instead of a second tap.
  useEffect(() => {
    if (mobileSearchOpen) mobileSearchInputRef.current?.focus();
  }, [mobileSearchOpen]);

  // Close the mobile search overlay on outside click/tap, and on Escape.
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMobileSearchOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSearchOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [mobileSearchOpen]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearchQuery(q); // instant — the input itself never waits on navigation

    // Debounce the actual navigation. Without this, every keystroke fired a
    // router.push() while on a non-home page (e.g. a tool page), so typing a
    // 6-letter query pushed 6 history entries and "back" had to be pressed 6
    // times to leave the search. Now navigation only fires once the user
    // pauses — by the time it does, if this is the first fire `pathname` is
    // still the non-home page (one push, one new history entry, so "back"
    // correctly returns to where they started); every fire after that has
    // already landed on the home page and uses replace, so further edits
    // never add more entries.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams(searchParams.toString());
      if (q) params.set('search', q);
      else params.delete('search');
      if (pathname !== home) router.push(`${home}?${params.toString()}`);
      else router.replace(`${home}?${params.toString()}`, { scroll: false });
    }, 250);
  };

  const clearSearch = () => {
    setSearchQuery('');
    const params = new URLSearchParams(searchParams.toString());
    params.delete('search');
    if (pathname === home) router.replace(home, { scroll: false });
    else router.push(home);
  };

  return (
    <header ref={headerRef} className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-borderDark transition-shadow duration-300 ease-out ${
      scrolled ? 'shadow-card' : 'shadow-none'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center gap-4">

          {/* Logo — hidden on mobile while search is expanded in-place */}
          <Link href={home} className={`flex-shrink-0 items-center group ${mobileSearchOpen ? 'hidden md:flex' : 'flex'}`}>
            <span className="font-heading font-bold text-2xl text-foreground tracking-tight">
              VIP<span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg,#7B2FF7,#F107A3)' }}>Toolkit</span>
            </span>
          </Link>

          {/* Search — desktop: always inline. Mobile: only rendered here
              once expanded (see toggle button below), and takes the full
              row in place of the logo/icons rather than adding a new row. */}
          <div className={`flex-1 min-w-0 max-w-md mx-4 ${mobileSearchOpen ? 'block' : 'hidden md:block'}`}>
            <div className="relative">
              <Search className="absolute start-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary pointer-events-none" />
              <input
                ref={mobileSearchInputRef}
                type="text"
                placeholder={dict.nav.searchPlaceholder}
                value={searchQuery}
                onChange={handleSearch}
                className="w-full ps-10 pe-10 py-2.5 bg-bg2 border border-borderDark rounded-xl text-sm text-foreground placeholder-textSecondary focus:outline-none focus:border-accent/60 focus:shadow-glow-purple transition-[border-color,box-shadow]"
              />
              {searchQuery && (
                <button onClick={clearSearch} type="button" className="absolute end-3 top-1/2 -translate-y-1/2 text-textSecondary hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 ms-auto">
            {/* Hidden entirely when only one locale is enabled — a dropdown
                with a single, already-selected option isn't a real choice. */}
            {enabledLocales.length > 1 && <LanguageSwitcher locale={locale} locales={enabledLocales} />}
            <ThemeToggle lightLabel={dict.nav.switchToLightMode} darkLabel={dict.nav.switchToDarkMode} />
          </nav>

          {/* Mobile — while search is expanded, everything else hides so
              the input gets the full row. Closing happens via outside
              click/tap or Escape (see the effect above) — no separate
              close button needed. */}
          <div className="flex items-center gap-1 ms-auto md:hidden">
            {!mobileSearchOpen && (
              <>
                <button
                  onClick={() => setMobileSearchOpen(true)}
                  type="button"
                  className="p-1.5 text-textSecondary"
                  aria-label={dict.nav.searchPlaceholder}
                >
                  <Search className="h-5 w-5" />
                </button>
                {enabledLocales.length > 1 && <LanguageSwitcher locale={locale} locales={enabledLocales} />}
                <ThemeToggle lightLabel={dict.nav.switchToLightMode} darkLabel={dict.nav.switchToDarkMode} />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
